<template>
	<div class="lead-form">
		<LoadingIndicator v-if="isLoading"></LoadingIndicator>

		<div
			v-if="!isOpenWaitlist && isMemberAssisted"
			class="row justify-content-center"
			data-testid="not-waitlist-msg"
		>
			<div class="col-8 text-center">
				<h3 class="h6">{{ clubData.marketingClubName }}, {{ clubData.state }}</h3>
				<hr class="my-3" />
				<p class="lead">Currently Not In Waitlist</p>
			</div>
		</div>

		<form v-else-if="!isLoading" @submit="onSubmit">
			<ContactForm>
				<template v-if="isOpenWaitlist" #additionalFields>
					<EnhancedFields
						:membership-types="membershipTypes"
						:starting-price="startingPrice"
						:interests="interests"
					/>
				</template>
				<template #touText>
					{{ consentText }}
				</template>
				<template #touLinks>
					<div class="ps-3 mt-1">
						<a
							href="https://my.lifetime.life/policy/terms-of-use.html"
							target="_blank"
							rel="noopener noreferrer"
							type="button"
							class="link-underlined small text-nowrap me-1 btn-primary"
						>
							Terms of Use
						</a>
						<a
							href="https://my.lifetime.life/policy/privacy-policy.html "
							target="_blank"
							rel="noopener noreferrer"
							type="button"
							class="link-underlined small text-nowrap btn-primary"
						>
							Privacy Policy
						</a>
					</div>
				</template>
			</ContactForm>
			<div class="d-grid gap-2">
				<button
					v-adobe-data-layer="joinADLConfig"
					class="btn btn-dark"
					data-testid="submit-btn"
					type="submit"
				>
					Join the waitlist
				</button>
			</div>
			<input name="email-template" type="hidden" :value="emailTemplate" />
		</form>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, onBeforeMount, type Ref } from 'vue';
import { useForm } from 'vee-validate';
import ContactForm from 'Lib/components/shared/ContactForm.vue';
import LoadingIndicator from 'Lib/components/shared/LoadingIndicator.vue';
import EnhancedFields from 'Lib/components/lead-form/EnhancedFields.vue';
import { aemServletService } from '@/services/aemServletService';
import { featureFlagService } from '@life-time-inc/enterprise-framework-core';
import { leadFormService } from '@/services/leadFormService';
import { getAuthoredContent } from '@/util/getAuthoredContent';
import { club } from '@/util/club';
import { logError } from '@/util/logger';
import { prerenderGateCheck } from '@/util/prerenderGateCheck';
import type {
	LeadFormAuthoredContent,
	IFormData,
	TMLeadFormAuthoredContent
} from 'Types/aemui/lead-form/aemuiLeadForm';
import type {
	AemuiWaitlistInterest,
	AemuiWaitlistItem
} from 'Types/aemui/content-fragments/contentFragment';
import type { ltFramework } from '@life-time-inc/enterprise-framework-core';
import vAdobeDataLayer from '@/directives/AdobeDataLayer';
const clubData: Ref<ltFramework.IClubDetailsFromDOM> = ref(club);
const isLoading = ref<boolean>(true);
const authoredContent: Ref<LeadFormAuthoredContent> = ref({});
const authoredTmContent: Ref<TMLeadFormAuthoredContent> = ref({});

const showEnhancedFields = featureFlagService.isEnabled('aemuiEnhancedWaitlist');
const isOpenWaitlist = computed(() => {
	return (
		showEnhancedFields &&
		clubData.value.open === 'open' &&
		clubData.value.webPreSaleWaitlist === '1'
	);
});

const isMemberAssisted = computed(() => {
	return authoredTmContent.value.memberAssisted === 'true';
});

const emailTemplate = computed(() => {
	if (clubData.value.open !== 'open' && clubData.value.webPreSaleWaitlist === '1') {
		return authoredContent.value.presaleEmailTemplate ?? '12305_36157';
	}
	return authoredContent.value.openEmailTemplate ?? '12305_59514';
});

const confirmationUrl = computed(() => {
	return authoredContent.value.confirmationUrl ?? '/join/waitlist/waitlist-confirmation.html';
});

const errorUrl = computed(() => {
	return authoredContent.value.errorUrl ?? '/error/lead.html';
});

const startingPrice: Ref<string | null> = ref(null);
const membershipTypes: Ref<AemuiWaitlistItem[]> = ref([]);
const interests: Ref<AemuiWaitlistInterest[]> = ref([]);
onBeforeMount(async () => {
	if (prerenderGateCheck()) {
		return;
	}

	authoredContent.value = getAuthoredContent<LeadFormAuthoredContent>('aemuiLeadFormScript');
	authoredTmContent.value = getAuthoredContent<TMLeadFormAuthoredContent>(
		'aemuiMemberAssistedLeadFormScript'
	);

	if (clubData.value.mmsClubId && isOpenWaitlist.value) {
		try {
			const response = await aemServletService.getWaitlistItems(clubData.value.mmsClubId);
			const interestRes = await aemServletService.getWaitlistInterests(clubData.value.mmsClubId);
			startingPrice.value = response.data.club.startingPriceString;
			membershipTypes.value = response.data.variations;
			interests.value = interestRes.data.variations;
		} catch (error: unknown) {
			logError('GET waitlist items failed', { clubId: clubData.value.mmsClubId }, error);
		}
	}
	isLoading.value = false;
});

const consentText =
	"I acknowledge that I am at least 18 years of age and agree to the Privacy Policy and Terms of Use and expressly consent to Life Time's use of this information to contact me, which may include sending me electronic commercial or promotional messages, which I may opt out of at any time.";

const { handleSubmit } = useForm<IFormData>({
	initialValues: {
		addressLatitude: '',
		addressLongitude: '',
		birthYear: '',
		channel: 'AEM',
		clubStatus: '',
		clubType: 'club',
		communicationConsent: '',
		consentDateAndTime: '',
		consentIPAddress: '',
		consentText: consentText,
		currentMember: '',
		dcmp: '',
		description: '',
		device: '',
		emailAddress: '',
		emailTemplate: emailTemplate.value,
		firstName: '',
		gcId: '',
		gcLId: '',
		group: '',
		inquiryType: 'Web Pre-Sale Waitlist',
		interests: '',
		keyWords: '',
		landingPage: `${document.URL}`,
		lastName: '',
		leadType: 'Web',
		membershipInfoRequested: 'Wait List',
		mmsClubId: '',
		operatingSystem: '',
		referringDomain: `${document.location.origin}`,
		referringPage: `${document.location.pathname}`,
		subject: '',
		telephone2: '',
		tou: false,
		utmAudience: '',
		utmCampaign: '',
		utmContent: '',
		utmImage: '',
		utmMedium: '',
		utmSource: '',
		utmTerm: '',
		visitCount: '',
		visitorId: ''
	}
});

const onSubmit = handleSubmit(async (values) => {
	// hidden values on the form we populate
	const url = new URL(window.location.href);

	values = {
		...values,
		consentDateAndTime: document.lastModified,
		emailTemplate: emailTemplate.value,
		interests: values.interests ?? '',
		mmsClubId: clubData.value.mmsClubId.toString(),
		utmAudience: url.searchParams.get('utmAudience') ?? '',
		utmCampaign: url.searchParams.get('utmCampaign') ?? '',
		utmContent: url.searchParams.get('utmContent') ?? '',
		utmImage: url.searchParams.get('utmImage') ?? '',
		utmMedium: url.searchParams.get('utmMedium') ?? '',
		utmSource: url.searchParams.get('utmSource') ?? '',
		utmTerm: url.searchParams.get('utmTerm') ?? ''
	};

	if (!isOpenWaitlist.value) {
		delete values.numberOfAdults;
		delete values.numberOfJuniors;
	}

	if (isOpenWaitlist.value) {
		values.inquiryType = 'Open Waitlist';
	}

	if (isMemberAssisted.value) {
		values.leadType = 'Self Generated';
		values.emailTemplate = '';
	}

	try {
		isLoading.value = true;
		await leadFormService.submitForm(values);
		window.location.assign(confirmationUrl.value);
	} catch (error: unknown) {
		logError('Lead Form submit error', { values }, error);
		window.location.assign(errorUrl.value);
	} finally {
		isLoading.value = false;
	}
});

const joinADLConfig = {
	name: 'leadform',
	type: 'button',
	title: 'Join the waitlist',
	link: confirmationUrl.value
};

defineExpose({ clubData, isLoading });
</script>
